import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, Receipt, XOctagon, ClipboardList, RefreshCw, BarChart3,
  Users, Settings, Percent, Building, KeyRound, Megaphone, BookOpen, Mail, ScrollText, LogOut, Plane, FileText, ToggleRight, Coins, Ticket, Globe2, Palette, Menu, X
} from "lucide-react";
import { AdminAuthProvider, useAdminAuth } from "@/lib/adminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["admin", "superadmin", "restricted_admin"]);

const groups = [
  {
    label: "Operations",
    items: [
      { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, perm: null },
      { to: "/admin/bookings", label: "Bookings", icon: Package, perm: "bookings" },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt, perm: "transactions" },
      { to: "/admin/cancel-requests", label: "Cancel Requests", icon: ClipboardList, perm: "cancel_requests" },
      { to: "/admin/cancellations", label: "Cancellations", icon: XOctagon, perm: "cancellations" },
      { to: "/admin/refunds", label: "Refunds", icon: RefreshCw, perm: "refunds" },
      { to: "/admin/reports", label: "Sales Reports", icon: BarChart3, perm: "reports" },
      { to: "/admin/reports/custom", label: "Custom Report", icon: FileText, perm: "reports" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/ads", label: "Ads / Banners", icon: Megaphone, perm: "ads" },
      { to: "/admin/packages", label: "Packages", icon: BookOpen, perm: "packages" },
      { to: "/admin/promos", label: "Promo Codes", icon: Percent, perm: "promos" },
      { to: "/admin/contact-messages", label: "Contact Messages", icon: Mail, perm: "contact_messages" },
      { to: "/admin/content/pages", label: "Pages", icon: FileText, perm: "settings" },
      { to: "/admin/branding", label: "Whitelabel branding", icon: Palette, perm: "branding" },
      { to: "/admin/settings", label: "Theme & Settings", icon: Settings, perm: "settings" },
    ],
  },
  {
    label: "Distribution",
    items: [
      { to: "/admin/service-catalog", label: "Service Catalog", icon: ToggleRight, perm: "settings" },
      { to: "/admin/currencies", label: "Currencies", icon: Globe2, perm: "currencies" },
      { to: "/admin/fx", label: "FX Rates", icon: Coins, perm: "fx_rates" },
      { to: "/admin/airline-commissions", label: "Airline Commissions", icon: Percent, perm: "airline_codes" },
      { to: "/admin/airline-tour-codes", label: "Airline Tour Codes", icon: Ticket, perm: "airline_codes" },
      { to: "/admin/airline-account-codes", label: "Airline Account Codes", icon: KeyRound, perm: "airline_codes" },
      { to: "/admin/account-code-audit", label: "Account Code Audit", icon: ScrollText, perm: "airline_codes" },
      { to: "/admin/supplier-logs", label: "Supplier Logs", icon: FileText, perm: "supplier_logs" },
      { to: "/admin/pricing-rules/markup", label: "Pricing Rules", icon: Percent, perm: "settings" },
      { to: "/admin/suppliers", label: "Suppliers / Airlines", icon: Building, perm: "settings" },
      { to: "/admin/airline-logos", label: "Airline Logos", icon: Plane, perm: "airline_logos" },
      { to: "/admin/api-master", label: "API Master", icon: KeyRound, perm: "settings" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/users/customers", label: "Customer Users", icon: Users, perm: "customers" },
      { to: "/admin/users/admins", label: "Admin Users", icon: Users, perm: "admins" },
      { to: "/admin/audit-logs", label: "Audit Log", icon: ScrollText, perm: "audit_logs" },
    ],
  },
];

export default function AdminLayout() {
  return (
    <AdminAuthProvider>
      <AdminLayoutInner />
    </AdminAuthProvider>
  );
}

function AdminLoginCard() {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (!u || !ADMIN_ROLES.has(u.role)) { toast.error("This account is not an admin."); return; }
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
      <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md w-full text-center space-y-4" data-testid="admin-login-card">
        <div className="w-12 h-12 mx-auto rounded-xl bg-[var(--brand-accent)] flex items-center justify-center"><Plane className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-[var(--brand-accent)]">Admin Backoffice</h1>
          <p className="text-sm text-slate-500 mt-1">Sign in with an admin account to continue.</p>
        </div>
        <div className="text-left space-y-3">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" required autoFocus
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" data-testid="admin-password-input" type="password" required minLength={6}
                   value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        </div>
        <Button data-testid="admin-signin-btn" type="submit" disabled={busy}
                className="w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
          {busy ? "Signing in..." : "Sign in"}
        </Button>
        <p className="text-xs text-slate-400">Demo: admin@flightaggregator.com / Admin@12345</p>
      </form>
    </div>
  );
}

function AdminLayoutInner() {
  const { user, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [myPerms, setMyPerms] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the mobile drawer whenever the route changes so navigating
  // from a menu item doesn't leave the drawer stuck open on top of the page.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await import("@/lib/api").then((m) => m.api.get("/admin/me/permissions"));
        setMyPerms(data);
      } catch (_e) {
        setMyPerms({ is_super_admin: true, permissions: {}, role: user.role });
      }
    })();
  }, [user]);

  if (loading) return <div className="p-10 text-slate-500">Loading...</div>;
  if (!user || !ADMIN_ROLES.has(user.role)) return <AdminLoginCard />;

  // Filter sidebar items by the signed-in user's read permission per module.
  // `perm=null` items (e.g. Overview) are always visible; a super-admin sees
  // everything regardless of the permissions map.
  const isSuper = !myPerms ? true : !!myPerms.is_super_admin;
  const perms = myPerms?.permissions || {};
  const canRead = (m) => isSuper || !m || perms[m] === "r" || perms[m] === "rw";
  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => canRead(it.perm)) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen bg-slate-50 md:flex">
      {/* ─── Mobile top bar (hamburger + logo) — hidden on md+ ─── */}
      <div className="md:hidden sticky top-0 z-30 bg-[var(--brand-accent)] text-white flex items-center justify-between px-4 h-14 shadow">
        <button aria-label="Open menu" data-testid="admin-mobile-menu"
                onClick={() => setMobileOpen(true)}
                className="p-2 -ml-2 rounded-lg hover:bg-white/10">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[var(--brand-primary)] flex items-center justify-center"><Plane className="w-4 h-4" /></div>
          <div>
            <div className="font-heading font-bold text-sm leading-tight">FlightAgg</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--brand-secondary)] leading-tight">Backoffice</div>
          </div>
        </div>
        <div className="w-9" />
      </div>

      {/* ─── Backdrop when drawer is open on mobile ─── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} data-testid="admin-mobile-backdrop" />
      )}

      {/* ─── Sidebar (drawer on mobile, static column on md+) ─── */}
      <aside data-testid="admin-sidebar"
             className={
               "bg-[var(--brand-accent)] text-white p-5 flex flex-col overflow-y-auto z-50 " +
               "fixed inset-y-0 left-0 w-72 max-w-[85vw] transform transition-transform duration-200 " +
               (mobileOpen ? "translate-x-0" : "-translate-x-full") + " " +
               "md:static md:translate-x-0 md:w-64 md:max-w-none md:z-auto md:shrink-0"
             }>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[var(--brand-primary)] flex items-center justify-center"><Plane className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-bold">FlightAgg</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-secondary)]">Backoffice</div>
            </div>
          </div>
          <button aria-label="Close menu" onClick={() => setMobileOpen(false)}
                  className="md:hidden p-1.5 rounded-lg hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-5 flex-1">
          {visibleGroups.map(g => (
            <div key={g.label}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-teal-200/70 mb-1.5 px-2">{g.label}</div>
              <nav className="space-y-0.5">
                {g.items.map(it => (
                  <NavLink key={it.to} to={it.to} end={it.end}
                    data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z]/g,'-')}`}
                    className={({ isActive }) => `flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-[13px] ${isActive ? "bg-white/15 text-white font-medium" : "text-teal-100/80 hover:bg-white/10"}`}>
                    <it.icon className="w-4 h-4" /> {it.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
          <div className="text-xs text-teal-100/70">Signed in as<br /><span className="text-white font-medium break-all">{user.email}</span></div>
          <Button data-testid="admin-logout-btn" variant="secondary" size="sm"
                  onClick={() => { logout(); navigate("/"); }}
                  className="w-full bg-white/10 hover:bg-white/20 text-white border-0">
            <LogOut className="w-3.5 h-3.5 mr-2" /> Logout
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-6 md:p-8 overflow-auto"><Outlet /></main>
    </div>
  );
}
